import {
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from "@mui/material";
import axios from "../../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import { useNavigate } from "react-router";
import {
    LOCAL_DATE_CONVERSION,
    LOCAL_PST_DATE_CONVERSION,
    MILLIS_TO_LOCALE,
    MILLIS_TO_LOCALE_DATE_TIME_FORMAT_POPUP,
    MILLIS_TO_LOCALE_DIGIT_FORMAT,
    dateDifference,
} from "../../../../utilities/helpers/formatUnix";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CancelIcon from "@mui/icons-material/Cancel";
import DoDisturbAltIcon from "@mui/icons-material/DoDisturbAlt";
import {
    ACTION,
    PERMISSIONS,
    usePermissions,
} from "../../../../utils/permissions";

dayjs.extend(utc);
dayjs.extend(timezone);

import moment from "moment-timezone";
import {
    DELETE_DRAFT_PROMO_SUCCESS,
    DELETE_PROMO_FAILED,
    DELETE_SCHEDULE_PROMO_SUCCESS,
    DRAFT_STATUS,
    LIVE,
    SCHEDULED_STATUS,
    UNPUBLISH_ERROR,
    UNPUBLISH_SUCCESS,
    UNSCHEDULE_ERROR,
    UNSCHEDULE_SUCCESS,
} from "../../LandingPage/constants";
import DeleteModal from "../../../UI/atoms/Popup/DeleteModal";

const PromoMappingTable = forwardRef(
    ({ status, showActionButton, refreshDraft }: any, ref: any) => {
        // hooks
        const navigate = useNavigate();
        const { hasPermission } = usePermissions();
        const [loader, setLoader] = useState(false);
        const { enqueueSnackbar } = useSnackbar();
        const [selectPromo, setSelectedPromo] = useState<any>(null);
        const [mappingList, setMappingList] = useState<any>([]);
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);
        const [totalCount, setTotalCount] = useState(0);
        const [skipKey, setSkipKey] = useState("");

        const [skipKeyQueue, setskipKeyQueue] = useState<any>([]);
        const [hoverId, setHoverId] = useState<any>(null);
        const [showCancelPopup, setShowCancelPopup] = useState(false);
        const [cancelLoader, setCancelLoader] = useState(false);
        const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

        const closeDeleteModal = () => {
            setIsDeleteModalOpen(false);
            setSelectedPromo(null);
        };

        useImperativeHandle(
            ref,
            () => ({
                getMappingList,
            }),
            []
        );

        useEffect(() => {
            getMappingList();
        }, []);

        const resetTableMeta = () => {
            setskipKeyQueue([]);
            setPage(0);
        };

        const getMappingList = async () => {
            const pages = await getPromoMappingList(status, rowsPerPage);
            setTotalCount(pages.totalCount);
            setMappingList([...pages.list]);
        };

        const getPromoMappingList = async (
            status: any,
            limit: number | string = rowsPerPage || 0,
            skip: string = skipKey || "",
            skipQueue: any = skipKeyQueue
        ) => {
            const pages = await getPromo(status, limit, skip);
            const list = pages?.list;
            list.length &&
                pushKey(
                    `${list[list.length - 1]?.mappingId}__${
                        status == DRAFT_STATUS
                            ? list[list.length - 1]?.updateAt
                            : list[list.length - 1]?.publishDateTime
                    }`,
                    skipQueue
                );
            return pages;
        };

        const getPromo = async (
            status: string,
            limit: number | string,
            skip: string = ""
        ) => {
            try {
                setLoader(true);
                const saveVersion = await axios.get(
                    `${process.env.BASE_URL}api/v1/cms/promo/filter?status=${status}&skip=${skip}&limit=${limit}`
                );
                return saveVersion.data?.data;
            } catch (error: any) {
                console.log(
                    "Error in getting master information",
                    error.response
                );
                enqueueSnackbar(error.response?.data?.message, {
                    variant: "warning",
                });
            } finally {
                setLoader(false);
            }
        };

        const closeCancelModal = () => {
            setShowCancelPopup(false);
            setSelectedPromo(null);
        };

        const openDeleteModal = (page: any) => {
            setSelectedPromo(page);
            setIsDeleteModalOpen(true);
        };

        const navigateToForm = (id: string) => {
            navigate("/promo-video-management/" + id);
        };

        const handleViewLive = (id: string) => {
            navigateToForm(id);
        };

        const handleCancelPage = (promo: any) => {
            setShowCancelPopup(true);
            setSelectedPromo(promo);
        };

        const handleMoveToDraft = () => {
            unschedulePromoMapping(selectPromo?.mappingId);
        };

        const unschedulePromoMapping = async (mappingId: any) => {
            try {
                setCancelLoader(true);
                const unschedule = await axios.get(
                    `${process.env.BASE_URL}api/v1/cms/promo/unschedule?reason=MANUALLY_UNSCHEDULED&mappingId=${mappingId}`
                );
                if (unschedule?.data?.success) {
                    resetTableMeta();
                    getMappingList();
                    refreshDraft();
                    enqueueSnackbar(
                        status == SCHEDULED_STATUS
                            ? UNSCHEDULE_SUCCESS
                            : UNPUBLISH_SUCCESS,
                        { variant: "success" }
                    );
                    closeCancelModal();
                } else {
                    enqueueSnackbar(
                        status == SCHEDULED_STATUS
                            ? UNSCHEDULE_ERROR
                            : UNPUBLISH_ERROR,
                        { variant: "error" }
                    );
                }
            } catch (error: any) {
                console.log("Error in unschedulePromoMapping", error);
                enqueueSnackbar(
                    status == SCHEDULED_STATUS
                        ? UNSCHEDULE_ERROR
                        : UNPUBLISH_ERROR,
                    { variant: "warning" }
                );
            } finally {
                setCancelLoader(false);
            }
        };

        const tableRowRender = () => {
            return (
                <TableRow>
                    <TableCell sx={{ width: 300 }}>
                        <h3>Promo Title</h3>
                    </TableCell>
                    <TableCell sx={{ width: 300 }}>
                        <h3>Asset Title</h3>
                    </TableCell>
                    <TableCell sx={{ width: 150 }}>
                        <h3>Status</h3>
                    </TableCell>
                    {status === LIVE || status === SCHEDULED_STATUS ? (
                        <>
                            <TableCell sx={{ width: 200 }}>
                                <h3>Publish Date</h3>
                            </TableCell>
                            <TableCell sx={{ width: 200 }}>
                                <h3>Unpublish Date</h3>
                            </TableCell>
                        </>
                    ) : (
                        <>
                            <TableCell sx={{ width: 400 }}>
                                <h3>Date</h3>
                            </TableCell>
                        </>
                    )}

                    <TableCell align="center" sx={{ width: 150 }}>
                        <h3>Actions</h3>
                    </TableCell>
                </TableRow>
            );
        };

        const handleChangePage = async (event: unknown, newPage: number) => {
            let skipKeyValue = "";
            let queue;
            if (newPage < page) {
                queue = popTwice();
                setskipKeyQueue([...queue]);
                skipKeyValue = queue[queue.length - 1] || "";
                console.log(skipKeyQueue);
            } else {
                skipKeyValue = skipKeyQueue[skipKeyQueue.length - 1];
            }
            console.log("newPage", newPage);
            setPage(newPage);
            const pages = await getPromoMappingList(
                status,
                rowsPerPage,
                skipKeyValue,
                queue
            );
            setMappingList([...pages.list]);
            setTotalCount(pages.totalCount);
        };

        const handleChangeRowsPerPage = async (
            event: React.ChangeEvent<HTMLInputElement>
        ) => {
            resetTableMeta();
            setRowsPerPage(parseInt(event.target.value, 10));
            const pages = await getPromoMappingList(
                status,
                event.target.value,
                undefined,
                []
            );
            setMappingList([...pages.list]);
            setTotalCount(pages.totalCount);
        };

        const pushKey = (key: any, queue: any = skipKeyQueue) => {
            const skipValue = [...queue, key];
            setskipKeyQueue(skipValue);
        };

        const popKey = (queue: any[] = skipKeyQueue) => {
            const skipValue = [...queue];
            const popValue = skipValue.pop();
            setskipKeyQueue(skipValue);
            return skipValue;
        };

        const popTwice = () => {
            const queue1 = popKey();
            const queue = popKey(queue1);
            return queue;
        };

        const onHoverDate = (row: any, isEnter: boolean) => {
            if (isEnter && dateDifference(row.publishDate) < 31) {
                setHoverId(row.mappingId);
            } else {
                setHoverId(null);
            }
        };

        const showDateBasedOnStatus = (row: any) => {
            if (status == DRAFT_STATUS) {
                return (
                    <TableCell>
                        Saved On <br />
                        {MILLIS_TO_LOCALE_DIGIT_FORMAT(row.updateAt)}
                    </TableCell>
                );
            } else if (status == LIVE || status == SCHEDULED_STATUS) {
                return (
                    <>
                        <TableCell>
                            {status == LIVE ? "Published On" : "Scheduled for"}{" "}
                            <br />
                            {LOCAL_DATE_CONVERSION(row?.publishDateTime)}{" "}
                            {dayjs(row.publishDateTime).format("hh:mm A")}
                        </TableCell>
                        <TableCell>
                            Unpublish On <br />
                            {LOCAL_DATE_CONVERSION(row?.unpublishDateTime)}{" "}
                            {dayjs(row.unpublishDateTime).format("hh:mm A")}
                        </TableCell>
                    </>
                );
            }
        };

        const deleteMapping = async () => {
            try {
                setLoader(true);
                const deleteRail = await axios.delete(
                    `${process.env.BASE_URL}api/v1/cms/promo/${selectPromo.mappingId}`
                );
                if (deleteRail?.data?.success) {
                    enqueueSnackbar(
                        status == DRAFT_STATUS
                            ? DELETE_DRAFT_PROMO_SUCCESS
                            : DELETE_SCHEDULE_PROMO_SUCCESS,
                        { variant: "success" }
                    );
                    getMappingList();
                    closeDeleteModal();
                } else {
                    enqueueSnackbar(DELETE_PROMO_FAILED, { variant: "error" });
                }
            } catch (error: any) {
                enqueueSnackbar(DELETE_PROMO_FAILED, { variant: "error" });
                console.log("Error in getting master information", error);
            } finally {
                setLoader(false);
                setIsDeleteModalOpen(false);
            }
        };

        return (
            <div>
                <div>
                    <h4 className="text-capitalize">
                        {status}{" "}
                        {loader && (
                            <CircularProgress className="ml-1" size={24} />
                        )}
                    </h4>
                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>{tableRowRender()}</TableHead>
                            <TableBody>
                                {mappingList &&
                                    mappingList.map(
                                        (row: any, index: number) => (
                                            <TableRow
                                                key={row.mappingId}
                                                sx={{
                                                    "&:last-child td, &:last-child th":
                                                        { border: 0 },
                                                }}
                                            >
                                                <TableCell>
                                                    <h4>
                                                        {" "}
                                                        {row.promoVideoTitle}
                                                    </h4>
                                                    {row?.promoVideoId || ""}
                                                </TableCell>
                                                <TableCell>
                                                    <h4> {row.assetTitle}</h4>
                                                    {row?.assetId || ""}
                                                </TableCell>
                                                <TableCell className="text-capitalize">
                                                    {row?.publishStatus || "-"}
                                                </TableCell>

                                                {showDateBasedOnStatus(row)}

                                                <TableCell
                                                    sx={{
                                                        paddingRight: 0,
                                                        paddingLeft: 0,
                                                    }}
                                                    align="center"
                                                >
                                                    {status == LIVE && (
                                                        <>
                                                            <Tooltip
                                                                className="cursor-pointer"
                                                                title="View Details"
                                                            >
                                                                <VisibilityIcon
                                                                    className="action-icons"
                                                                    onClick={() =>
                                                                        handleViewLive(
                                                                            row.mappingId
                                                                        )
                                                                    }
                                                                />
                                                            </Tooltip>
                                                            {hasPermission(
                                                                PERMISSIONS.curation,
                                                                ACTION.EDIT
                                                            ) && (
                                                                <Tooltip
                                                                    className="cursor-pointer"
                                                                    title="Unpublish"
                                                                >
                                                                    <DoDisturbAltIcon
                                                                        className="action-icons"
                                                                        onClick={() =>
                                                                            handleCancelPage(
                                                                                row
                                                                            )
                                                                        }
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </>
                                                    )}

                                                    {status == DRAFT_STATUS && (
                                                        <>
                                                            <Tooltip
                                                                className="cursor-pointer"
                                                                title="Edit Detail"
                                                            >
                                                                <EditIcon
                                                                    className="action-icons"
                                                                    onClick={() =>
                                                                        navigateToForm(
                                                                            row.mappingId
                                                                        )
                                                                    }
                                                                />
                                                            </Tooltip>
                                                            {hasPermission(
                                                                PERMISSIONS.curation,
                                                                ACTION.EDIT
                                                            ) && (
                                                                <Tooltip
                                                                    className="cursor-pointer"
                                                                    title="Delete"
                                                                >
                                                                    <DeleteIcon
                                                                        className="action-icons"
                                                                        onClick={() =>
                                                                            openDeleteModal(
                                                                                row
                                                                            )
                                                                        }
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </>
                                                    )}
                                                    {hasPermission(
                                                        PERMISSIONS.curation,
                                                        ACTION.EDIT
                                                    ) &&
                                                        status ==
                                                            SCHEDULED_STATUS && (
                                                            <>
                                                                <Tooltip
                                                                    className="cursor-pointer"
                                                                    title="Unschedule"
                                                                >
                                                                    <CancelIcon
                                                                        className="action-icons"
                                                                        onClick={() =>
                                                                            handleCancelPage(
                                                                                row
                                                                            )
                                                                        }
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip
                                                                    className="cursor-pointer"
                                                                    title="Delete"
                                                                >
                                                                    <DeleteIcon
                                                                        className="action-icons"
                                                                        onClick={() =>
                                                                            openDeleteModal(
                                                                                row
                                                                            )
                                                                        }
                                                                    />
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </div>
                <DeleteModal
                    title={
                        status == SCHEDULED_STATUS
                            ? "Unschedule Mapping?"
                            : "Unpublish Mapping?"
                    }
                    isOpen={showCancelPopup}
                    handleClose={closeCancelModal}
                    handleConfirm={handleMoveToDraft}
                    cancelLabel="Cancel"
                    confirmLabel={
                        status == SCHEDULED_STATUS ? "Unschedule" : "Unpublish"
                    }
                    confirmButtonClassName="bg-error text-white"
                >
                    {status == SCHEDULED_STATUS ? (
                        <>
                            <p>
                                Are you sure you want to Unschedule selected
                                scheduled mapping?
                            </p>
                        </>
                    ) : (
                        <>
                            <p>
                                Are you sure you want to Unpublish selected live
                                mapping?
                            </p>
                            <p>
                                You can find it under Draft section post
                                unpublishing
                            </p>
                        </>
                    )}
                </DeleteModal>
                <DeleteModal
                    title="Delete Mapping?"
                    isOpen={isDeleteModalOpen}
                    handleClose={closeDeleteModal}
                    handleConfirm={deleteMapping}
                >
                    <p>
                        Are you sure you want to delete selected{" "}
                        {status === DRAFT_STATUS ? "draft" : "scheduled"}{" "}
                        mapping?
                    </p>
                </DeleteModal>
            </div>
        );
    }
);

export default PromoMappingTable;
